<template>
  <div class="pthvSchedule-widget">
    <ApthScheduleForm v-if="!line && !eventsHandlerName"
      :lines="lines"
      :stopsByLineCategorised="stopsByLineCategorised"
      :stops="stops"
      :withDestinations="withDestinations"
      :hasCategories="hasCategories"
      @selectLine="selectLine"
      @nextDepartureClick="nextDepartureClick"
    />
    <ApthScheduleSelectStop
      :stops="lineStops"
      :ariaControlItemId="ariaControlItemId"
      :isLoading="isLoading"
      :minDate="minDate"
      :maxDate="maxDate"
      @selectStop="selectStop"
      @nextDepartureClick="nextLineDepartureClick"
    />
    <div v-if="isLoading" class="loader">{{ $t("loading") }}</div>
    <ApthScheduleListing
        v-show="showListing" :destinations="destinations" :allLines="allLines"
        :id="ariaControlItemId" :showOccupancy="showOccupancy"
        :horizontalSchedules="horizontalSchedules" :indicators="indicators" :showTableHeader="false"
        :showMoreLink="false" :alwaysShowHours="alwaysShowHours"
        role="region" aria-live="polite"
      />
  </div>
</template>

<script>
import Vue from 'vue'
//Components
import ApthScheduleForm from "./schedule/forms/ApthScheduleForm";
import ApthScheduleSelectStop from "./schedule/forms/ApthScheduleSelectStop";
import ApthScheduleListing from "./ApthScheduleListing";

//Support functions
// import initData from "../lib/initData.ts";
import { clearExpiredCacheStorage } from "../lib/utils.ts";
import initData from "../lib/initDataProto.ts";
import getStopsByLines from "../lib/getStopsByLines.ts";
import getStopsByLineCategorised from "../lib/getStopsByLineCategorised.ts";
import getLineStops from "../lib/getLineStops.ts";
import departuresFromGtfs from "../lib/departuresFromGtfs.ts";
import getCalendarPerimeter from "../lib/getCalendarPerimeter.ts";

import { Destination } from "../lib/types.ts";
import { Line } from "../lib/types/line.ts";

export default {
  name: "ApthScheduleWidget",
  created() {
    this.isLoading = true;
    // Get a promise for the calendar perimeter
    const getCalendarPerimeterPromise = getCalendarPerimeter(this.gtfsUri, this.encodeUriPath, this.network).then(data => {
      if (data && data.error == undefined) {
        this.minDate = data.start;
        this.maxDate = data.end;
      }
    });
    // Get a promise for data initialization
    const initDataPromise = async () => {
      if (this.eventsHandlerName){
        // this.eventsBus = window.scheduleWidgetEventsBus; // so that legacy javascript can send events to this widget
        this.eventsBus = window[this.eventsHandlerName]; // so that legacy javascript can send events to this widget
        this.isLoading = false;
        if (this.eventsBus){
            this.eventsBus.$on('lineChanged', (line) => {
              this.lineref = line;
              this.isLoading = true;
              getLineStops(this.gtfsUri, this.encodeUriPath, this.network, line).then(this.setLineStops);
            })
          }
        } else if (this.line){
            this.lineref = this.line;
            getLineStops(this.gtfsUri, this.encodeUriPath, this.network, this.line).then(data => {
              this.lineStops = data;
            });
        } else {
            initData(this.gtfsUri, this.encodeUriPath, this.network).then(data => {
              let stopsByLine = getStopsByLines(data);
              this.stopsByLineCategorised = getStopsByLineCategorised(stopsByLine);
              this.hasCategories = this.stopsByLineCategorised.length > 1;
              this.lines = data.lines;
              this.stops = data.stops;
          });
        }
    }
    const initScheduleData = async () => {
      await Promise.all([getCalendarPerimeterPromise, initDataPromise()]);
    }
    clearExpiredCacheStorage(this.gtfsUri, this.encodeUriPath, this.network).then((expirationData) => {
        initScheduleData().then(_ => {
          this.isLoading = false;
        });
      }
    );
  },
  data() {
    return {
      lineref: null,
      isLoading: false,
      showListing: false,
      stopsByLineCategorised: [],
      hasCategories: false,
      lines: {},
      hasEventLine: false,
      stops: {},
      lineStops: [],
      // selectedLine: {},
      destinations: [],
      eventsBus: false,
      ariaControlItemId: 'pthv-schedule-listing-' + this._uid,
      minDate: null,
      maxDate: null,
    };
  },
  components: {
    ApthScheduleForm,
    ApthScheduleSelectStop,
    ApthScheduleListing
  },
  props: {
    gtfsUri: {
      type: String,
      required: true
    },
    encodeUriPath: {
      type: Boolean,
      required: false,
      default: false
    },
    network: {
      type: String,
      required: true
    },
    eventsHandlerName: {
      type: String,
      required: false,
    },
    withDestinations: {
      type: Boolean,
      required: false,
      default: true
    },
    allLines: {
      type: Boolean,
      required: false,
      default: true
    },
    showOccupancy: {
      type: Boolean,
      required: false,
      default: false,
    },
    simulateOccupancy: {
      type: Boolean,
      required: false,
      default: false,
    },
    horizontalSchedules: {
      type: Boolean,
      required: false,
      default: false,
    },
    line: {
      type: String,
      required: false
    },
    maxTimes: {
      type: Number,
      required: false
    },
    numberPastSchedules: {
      type: Number,
      required: false,
      default: 0,
    },
    pastSchedulesSince: {
      type: Number,
      required: false,
      default: 480,
    },
    alwaysShowHours: {
      type: Boolean,
      required: false,
      default: false,
    },
    indicators: {
      type: Object,
      required: false,
      default: () => {
        return {
          'scheduledService': true,
          'realTime': true,
          'disturbed': false,
          'diverted': false,
          'handicap': false,
          'departure': false,
          'terminus': false
        };
      },
    }
  },
  methods: {
    setLineStops: function(lineStops) {
      this.lineStops = lineStops;
      this.isLoading = false;
      this.hasEventLine = true;
      this.showListing = false;
    },
    selectStop: function(selStop) {
      // Change of stop
      this.showListing = false;
    },
    selectLine: function(selLine) {
      this.isLoading = true;
      this.lineref = selLine.ref;
      this.showListing = false;
      getLineStops(this.gtfsUri, this.encodeUriPath, this.network, this.lineref).then(data => {
        this.setLineStops(data);
        this.isLoading = false;
      });
    },
    nextDepartureClick: function(selStops) {
      this.showListing = false;
      this.isLoading = true;
      let stops = selStops.map(s => this.stops[s]);
      let that = this;
      departuresFromGtfs(
        this.gtfsUri,
        this.encodeUriPath,
        this.network,
        stops,
        this.lines
      ).then(destinations => {
        that.destinations = (that.allLines != "false" && (that.allLines != false))
          ? destinations
          : destinations.filter(
              // dest => Object.values(dest.line.refs).indexOf(that.selectedLine.ref) >= 0
              dest => Object.values(dest.line.refs).indexOf(that.lineref) >= 0
            );
          that.isLoading = false;
          that.showListing = true;
      });
    },
    stopPointFromRaw: function(lstop){
      // On retourne un type StopPoint
      let lines = lstop.Lines ? Object.keys(lstop.Lines) : [];
      let spoints = [];
      if (lstop.spoints){
        spoints = Object.values(lstop.spoints).map(spoint => {
          return {
            name: spoint.name,
            x: spoint.lat,
            y: spoint.lon,
            ref: spoint.id,
            city: spoint.city,
            stopId: spoint.id,//XXX : même chose ?
            lines: [],
            dests: [],
          }
        });
      }
      return {
        name: lstop.StopName.value,
        x: lstop.Location.Latitude,
        y: lstop.Location.Longitude,
        ref: lstop.StopPointRef,
        city: lstop.StopCity,
        stopId: lstop.StopPointRef,//XXX : même chose ?
        lines: lines,
        dests: [],
        spoints: spoints
      };
    },
    nextLineDepartureClick: function(selData) {
      this.showListing = false;
      this.isLoading = true;
      let selDate = selData.date;
      let selTime = selData.time;
      let selStops = selData.stop;
      let stops = selStops.map(s => {
        let stop = this.lineStops.find(ls => ls.StopPointRef == s);
        return this.stopPointFromRaw(stop);
      });
      let that = this;

      let line = {};
      line.ref = this.lineref;
      let lines = {};
      lines[line.ref] = line;

      // let nbTimes = (that.allLines != "false" && (that.allLines != false)) ? 2 : (this.maxTimes ? this.maxTimes : 5);
      let nbTimes = this.maxTimes ? this.maxTimes : 5;
      let options = {
        date: selDate,
        time: selTime,
        limit: nbTimes,
        nbpasttimes: this.numberPastSchedules,
        pastsince: this.pastSchedulesSince,
        simulateOccupancy: this.simulateOccupancy,
      };
      departuresFromGtfs(
        this.gtfsUri,
        this.encodeUriPath,
        this.network,
        stops,
        lines,
        options,
      ).then(destinations => {
        that.destinations = (that.allLines != "false" && (that.allLines != false))
          ? destinations
          : destinations.filter(
              dest => Object.values(dest.line.refs).indexOf(that.lineref) >= 0
            );
        that.isLoading = false;
        that.showListing = true;
      });
    }
  }
};
</script>

<style lang="scss">
/* @todo : put this style in a specific SCSS imported also by aroundMeWidget */
.loader {
  font-size: 0;
  width: 4rem;
  height: 4rem;
  background: url('../assets/loader.svg') no-repeat;
  background-size: contain;
}
</style>
